import { all, call, put, select, takeEvery } from 'redux-saga/effects';
import {
  createFormErrorMessage,
  pushFormattedError
} from 'helpers/formValidationHelper/formValidationMessage';
import {
  createUser,
  getAvailablePolicies,
  getCountriesDropdown,
  getUserPolicies,
  getUsers as getusersApi,
  searchUsers,
  setUserPolicies,
  updateUser
} from 'api/users';
import {
  getCurrentPage,
  getLimit,
  getSearch,
  getUpdateUserRequest,
  getUpdatedPolicyIds,
  getUserId
} from './usersSelectors';
import { getAuth } from 'actions/auth/login';
import { getGroupById } from 'api/groups';
import { getLocale } from 'actions/language/selectors';
import { pushAttentionNotification } from 'actions/notification/notification';

/*** Actions ***/
export const ADD_USER = 'ACCOUNTS/USERS/ADD_USER';
export const CHANGE_PAGE = 'UI/ACCOUNTS/USERS/CHANGE_PAGE';
export const FETCH_USER_POLICIES = 'ACCOUNTS/USERS/FETCH_USER_POLICIES';
export const GET_ALL_POLICIES_SUCCESS =
  'ACCOUNTS/USERS/GET_ALL_POLICIES_SUCCESS';
export const GET_POLICIES_SUCCESS = 'ACCOUNTS/USERS/GET_POLICIES_SUCCESS';
export const GET_USERS = 'ACCOUNTS/USERS/GET_USERS';
export const SEND_UPDATE = 'ACCOUNTS/USERS/SEND_UPDATE';
export const SET_LIMIT = 'UI/ACCOUNTS/USERS/SET_LIMIT';
export const SET_SEARCH_TEXT = 'UI/ACCOUNTS/USERS/SEARCH/SET_SEARCH_TEXT';
export const SET_USERS = 'ACCOUNTS/USERS/SET_USERS';
export const TOGGLE_ADD_USER_MODAL = 'UI/ACCOUNTS/USERS/TOGGLE_ADD_USER_MODAL';

export const TOGGLE_LIMIT_DROPDOWN = 'UI/ACCOUNTS/USERS/TOGGLE_LIMIT_DROPDOWN';
export const TOGGLE_SEARCH_FIELD_DROPDOWN =
  'UI/ACCOUNTS/USERS/SEARCH/TOGGLE_SEARCH_FIELD_DROPDOWN';
export const UPDATE_POLICIES = 'ACCOUNTS/USERS/UPDATE_POLICIES';
export const UPDATE_USER_SUCCESS = 'ACCOUNTS/USERS/UPDATE_USER_SUCCESS';

export const CHECK_SHAW_POLICY_ACCESS =
  'ACCOUNTS/USERS/CHECK_SHAW_POLICY_ACCESS';
export const FILTER_SHAW_POLICY = 'ACCOUNTS/USERS/FILTER_SHAW_POLICY';

/*** Action Creators ***/
const fetchUsersSuccess = (data, countries) => ({
  type: SET_USERS,
  countries,
  ...data
});

export const changePage = (page) => ({
  type: CHANGE_PAGE,
  page
});

export const toggleLimitDropdown = () => ({
  type: TOGGLE_LIMIT_DROPDOWN
});

export const getUsers = () => ({
  type: GET_USERS
});

export const createNewUser = (newUserData, onSuccess, onError) => ({
  type: ADD_USER,
  newUserData,
  onSuccess,
  onError
});

export const setSearchText = (search) => ({
  type: SET_SEARCH_TEXT,
  search
});

export const setLimit = (limit) => ({
  type: SET_LIMIT,
  limit
});

export const sendUpdate = (index, updatedUserData, onSuccess, onError) => ({
  type: SEND_UPDATE,
  index,
  updatedUserData,
  onSuccess,
  onError
});

export const toggleAddUserModal = () => ({
  type: TOGGLE_ADD_USER_MODAL
});

export const getAllPoliciesSuccess = (policies) => ({
  type: GET_ALL_POLICIES_SUCCESS,
  policies: policies.policies
});

export const updatePolicies = (index, policies) => ({
  type: UPDATE_POLICIES,
  index,
  policies
});

export const fetchUserPolicies = (index) => ({
  type: FETCH_USER_POLICIES,
  index
});

export const updateUserSuccess = (member, index) => ({
  type: UPDATE_USER_SUCCESS,
  member,
  index
});

export const checkSHAWPolicyAccess = (userID) => ({
  type: CHECK_SHAW_POLICY_ACCESS,
  userID
});

const filterSHAWPolicy = (shouldFilterOutSHAWPolicy) => ({
  type: FILTER_SHAW_POLICY,
  shouldFilterOutSHAWPolicy
});

/*** Reducers ***/
export const DEFAULT_STATE = {
  members: [],
  isOpen: [],
  currentPage: 0,
  totalPages: 1,
  limit: 10,
  limitDropdownOpen: false,
  total: 2,
  countries: [],
  allPolicies: [],
  shouldFilterOutSHAWPolicy: false,
  isModalOpen: false,
  search: {
    text: '',
    fields: [],
    searchAllFields: true
  }
};

export default function UsersReducer(
  state = DEFAULT_STATE,
  {
    members,
    member,
    total_pages,
    total,
    limit,
    page,
    type,
    search,
    index,
    countries,
    policies,
    shouldFilterOutSHAWPolicy
  } = {}
) {
  switch (type) {
    case SET_USERS: {
      return {
        ...state,
        members: members,
        totalPages: total_pages,
        total: total,
        countries: countries,

        isOpen: members.map(() => false)
      };
    }

    case SET_LIMIT: {
      return {
        ...state,
        limit: limit,
        currentPage: 0
      };
    }
    case CHANGE_PAGE: {
      return {
        ...state,
        currentPage: page
      };
    }
    case TOGGLE_LIMIT_DROPDOWN: {
      return {
        ...state,
        limitDropdownOpen: !state.limitDropdownOpen
      };
    }

    case UPDATE_USER_SUCCESS: {
      return {
        ...state,
        members: state.members.map((item, i) =>
          i === index ? { ...item, ...member } : item
        )
      };
    }
    case SET_SEARCH_TEXT: {
      return {
        ...state,
        search: search
      };
    }
    case GET_ALL_POLICIES_SUCCESS: {
      return {
        ...state,
        allPolicies: policies
      };
    }
    case UPDATE_POLICIES: {
      return {
        ...state,
        members: state.members.map((item, i) =>
          i === index ? { ...item, policies } : item
        )
      };
    }
    case TOGGLE_ADD_USER_MODAL: {
      return {
        ...state,
        isModalOpen: !state.isModalOpen
      };
    }
    case FILTER_SHAW_POLICY: {
      return {
        ...state,
        shouldFilterOutSHAWPolicy
      };
    }
    default: {
      return state;
    }
  }
}

/*** Sagas ***/
export function* fetchUsersWorker() {
  try {
    const auth = yield select(getAuth);
    const locale = yield select(getLocale);
    const page = yield select(getCurrentPage);
    const limit = yield select(getLimit);
    const countries = yield call(getCountriesDropdown, locale, auth);
    const search = yield select(getSearch);

    let data;
    if (search.text.length >= 1) {
      data = yield call(searchUsers, locale, auth, page, limit, search);
    } else {
      data = yield call(getusersApi, locale, auth, page, limit);
    }

    if (!search.fields.length && !search.searchAllFields) {
      data = {
        ...data,
        members: [],
        total: 0,
        total_pages: 0
      };
    }

    yield put(fetchUsersSuccess(data, countries && countries.countries));

    const policies = yield call(getAvailablePolicies, locale, auth);
    yield put(getAllPoliciesSuccess(policies));
  } catch (err) {
    yield pushFormattedError(err);
  }
}

export function* updateUserWorker(action) {
  try {
    const auth = yield select(getAuth);
    const locale = yield select(getLocale);

    const user = yield select(getUpdateUserRequest, action);

    // if getUpdatedUserRequest returns null, that means the action data
    // was empty, signifying that the user clicked save without making any changes
    // if this is the case, then do nothing
    if (user) {
      const data = yield call(updateUser, locale, auth, user);
      const id = yield select(getUserId, action);
      const updatedPolicyIds = yield select(
        getUpdatedPolicyIds,
        action.updatedUserData
      );

      if (updatedPolicyIds) {
        yield call(setUserPolicies, locale, auth, id, updatedPolicyIds);
      }

      yield put(updateUserSuccess(data, action.index));
      yield put(fetchUserPolicies(action.index));

      yield put(
        pushAttentionNotification({
          title: 'Success',
          messages: 'Changes saved.'
        })
      );
      action.onSuccess();
    }
  } catch (err) {
    if (err.messages) {
      let messageErrorCategories = Array.from(
        new Set(err.messages.map((error) => error.key))
      );
      action.onError(messageErrorCategories);
    }

    yield pushFormattedError(err, createFormErrorMessage);
  }
}

export function* fetchPoliciesWorker(action) {
  try {
    const auth = yield select(getAuth);
    const locale = yield select(getLocale);
    const id = yield select(getUserId, action);
    const data = yield call(getUserPolicies, locale, auth, id);

    yield put(updatePolicies(action.index, data.policies));
  } catch (err) {
    yield pushFormattedError(err);
  }
}

export function* createUserWorker(action) {
  try {
    const auth = yield select(getAuth);
    const locale = yield select(getLocale);

    yield call(createUser, locale, auth, action.newUserData);
    action.onSuccess();

    yield put(
      pushAttentionNotification({
        title: 'Success',
        messages: 'New user created.'
      })
    );
    yield put(toggleAddUserModal());
    yield put(getUsers());
  } catch (err) {
    if (err.messages) {
      let messageErrorCategories = Array.from(
        new Set(err.messages.map((error) => error.key))
      );
      action.onError(messageErrorCategories);
    }

    yield pushFormattedError(err, createFormErrorMessage);
  }
}

export function* checkUserInRootGroup(action) {
  const auth = yield select(getAuth);
  const locale = yield select(getLocale);
  const { parent } = yield call(getGroupById, locale, auth, action.userID);

  const isInRootGroup = parent == null;

  yield put(filterSHAWPolicy(!isInRootGroup));
}

export function* fetchUsersSaga() {
  yield all([
    takeEvery(SET_LIMIT, fetchUsersWorker),
    takeEvery(GET_USERS, fetchUsersWorker),
    takeEvery(CHANGE_PAGE, fetchUsersWorker),
    takeEvery(SEND_UPDATE, updateUserWorker),
    takeEvery(ADD_USER, createUserWorker),
    takeEvery(FETCH_USER_POLICIES, fetchPoliciesWorker),
    takeEvery(CHECK_SHAW_POLICY_ACCESS, checkUserInRootGroup)
  ]);
}



// WEBPACK FOOTER //
// ./src/actions/accounts/users/usersActions.js